import { Paper, Button, Theme, Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useState, useRef, useImperativeHandle, forwardRef, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormLabel,
  InputType,
  TextV2,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { SearchFilterProps, SelectTypes } from './SearchFilter.types';

import {
  CheckBox,
  FieldLabel,
  MinCharacterWarning,
  OverheadSearchLabelContainer,
  SearchLabelContainer,
  StyledForm,
} from './Components/SearchFilterStyledComponents';
import SearchInput from './Components/SearchInput';
import { isSelectEnabled } from './SearchFilter.helper';
import { getFormLabel } from './Components/Label';
import { LeftSelect } from './Components/LeftSelect';
import { RightSelect } from './Components/RightSelect';

const RawSearchFilterV2 = <T extends Partial<SelectTypes> = object>(
  {
    sx,
    placeholder,
    handleSearch,
    handleTermChange,
    showButton = true,
    allowEmptySearches = true,
    inputState,
    initialValue = '',
    inputType = InputType.TEXT,
    minCharacters,
    checkbox,
    isLabelAboveSearchInput = false,
    leftSelectProps,
    rightSelectProps,
    labelText,
    labelDropdownProps,
  }: SearchFilterProps<T>,
  ref?: Ref<any>,
) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState(initialValue);

  const showLeftSelect =
    leftSelectProps &&
    isSelectEnabled({
      selectProps: leftSelectProps,
    });

  const showRightSelect =
    rightSelectProps &&
    isSelectEnabled({
      selectProps: rightSelectProps,
    });
  const showLabelDropdown =
    labelDropdownProps && isSelectEnabled({ selectProps: labelDropdownProps });

  const searchInputRef = useRef<HTMLInputElement>(null);

  const clearSearchNow = () => {
    setSearchTerm('');
    if (handleSearch) {
      handleSearch('');
    }

    if (handleTermChange) {
      handleTermChange('');
    }
  };

  const handleSearchChange = (e: React.FormEvent<HTMLInputElement>) => {
    setSearchTerm(e.currentTarget.value);
    if (handleTermChange) {
      handleTermChange(e.currentTarget.value);
    }
  };

  const handleSearchKeypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      clearSearchNow();
    }
    if (e.key === 'Enter' && handleSearch) {
      handleSearch(searchTerm);
    }
  };

  useImperativeHandle(ref, () => ({
    focus: () => {
      searchInputRef.current?.focus();
    },
  }));

  const shouldShowClearButton = !!(
    Number(minCharacters) > 0 &&
    inputState &&
    inputState?.length >= Number(minCharacters)
  );
  const handleOnChangeTerm = (value: string) =>
    handleSearchChange({
      currentTarget: {
        value,
      },
    } as any);
  return (
    <Grid
      container
      sx={{
        alignItems: 'baseline',
        justifyContent: 'space-between',
        margin: '22px 0 32px',
        '& > div': {
          padding: `12px 24px !important`,
        },
      }}
    >
      <Paper
        sx={[
          () => ({
            flexGrow: 1,
            margin: '0 !important',
          }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
          }}
        >
          {isLabelAboveSearchInput && (
            <Grid
              container
              sx={{
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <OverheadSearchLabelContainer>
                <FormLabel
                  sx={{ alignSelf: 'center' }}
                  label={
                    <SearchLabelContainer>
                      <FieldLabel
                        sx={(theme: Theme) => ({
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing(0.5),
                        })}
                      >
                        <TextV2 component="p" variant="bodyBold">
                          {labelText}
                        </TextV2>
                      </FieldLabel>
                    </SearchLabelContainer>
                  }
                  htmlFor="search-filter-input"
                />
              </OverheadSearchLabelContainer>
              {checkbox?.label && <CheckBox {...checkbox} />}
            </Grid>
          )}

          <Grid
            wrap="nowrap"
            container
            size={8}
            sx={{
              justifyContent: 'flex-start',
            }}
          >
            <StyledForm
              autoComplete="off"
              onSubmit={(event) => event.preventDefault()}
              sx={{ width: '100%' }}
            >
              {!isLabelAboveSearchInput &&
                getFormLabel<T['labelSelectOptionsType']>({
                  showLabelDropdown: !!showLabelDropdown,
                  labelText,
                  ...labelDropdownProps,
                })}
              {showLeftSelect && (
                <LeftSelect<T['leftSelectOptionsType']> {...leftSelectProps} />
              )}
              <Box>
                <SearchInput
                  autoFocus
                  ref={searchInputRef}
                  id="search-filter-input"
                  name="search"
                  testId="search-input"
                  placeholder={placeholder}
                  value={inputState ?? searchTerm}
                  onChange={handleOnChangeTerm}
                  onKeyDown={handleSearchKeypress}
                  type={inputType}
                  onClear={() => {
                    clearSearchNow();
                  }}
                  clearText={t('ui.event-management.list-filter.clear')}
                  showClearNowButton={shouldShowClearButton}
                />
                {minCharacters &&
                inputState &&
                inputState?.length > 0 &&
                minCharacters > inputState?.trim().length ? (
                  <MinCharacterWarning>
                    {t('ui.event-management.list-filter.min-characters', {
                      count: minCharacters,
                    })}
                  </MinCharacterWarning>
                ) : null}
              </Box>
              {showRightSelect && (
                <RightSelect<T['rightSelectOptionsType']>
                  {...rightSelectProps}
                />
              )}
            </StyledForm>
          </Grid>
          {checkbox?.label && !isLabelAboveSearchInput && (
            <CheckBox {...checkbox} />
          )}
          {showButton ? (
            <Grid
              sx={{
                '& > * + *': {
                  marginLeft: spacingSizeMap.S,
                },
              }}
              size={{ xs: 12, md: 'auto' }}
            >
              <Button
                onClick={() => {
                  if (handleSearch) {
                    handleSearch(searchTerm);
                  }
                }}
                variant="contained"
                color="primary"
                data-testid="filter-search-button"
                disabled={!allowEmptySearches && searchTerm === ''}
              >
                {t('ui.event-management.list-filter.search')}
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Paper>
    </Grid>
  );
};

export const SearchFilter = forwardRef(RawSearchFilterV2) as <
  FilterType extends Partial<SelectTypes>,
>(
  props: SearchFilterProps<FilterType> & { ref?: Ref<any> },
) => React.ReactElement;
